"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";

interface HeaderData {
  background_color?: string;
  logo?: string;
  logo_alt_text?: string;
  /** A real boolean from the API (server/lib/headerFlags.js); missing means "on". */
  logo_redirect_enabled?: boolean | null;
  promotional_text?: string;
  promotional_text_bg_color?: string;
  promotional_text_color?: string;
  phone_icon?: string;
  phone_icon_alt?: string;
  phone_number?: string;
  phone_number_text_color?: string;
  button_name?: string;
  button_link?: string;
  button_color?: string;
  button_text_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
}

export default function DesktopHeader({
  headerData,
}: {
  headerData: HeaderData;
}) {
  const {
    background_color = "#ffffff",
    logo,
    logo_alt_text,
    promotional_text = "",
    promotional_text_bg_color = "#BE2330",
    promotional_text_color = "#FFFFFF",
    phone_icon,
    phone_icon_alt,
    phone_number = "",
    phone_number_text_color = "#000000",
    button_name = "Chat on WhatsApp",
    button_link = "",
    button_color = "#25D366",
    button_text_color = "#FFFFFF",
    button_icon,
    button_icon_alt,
  } = headerData || {};

  /**
   * Logo → "/" redirect. The API already resolves MySQL's 0/1/NULL to a boolean
   * (NULL → true); a missing value here (e.g. an unsaved admin preview) also stays
   * ON. Only an explicit false drops the anchor.
   */
  const logoRedirects = headerData?.logo_redirect_enabled !== false;

  /** Shared by both logo wrappers so the linked and unlinked forms are identical in size. */
  const logoBoxClass = cn(
    "bg-left bg-no-repeat bg-[length:100%_96%] rounded-spacing-14 shrink-0",
    "sm:h-[52px] sm:w-[152px]",
    "md:h-[60px] md:w-[174px]",
    "lg:h-[68px] lg:w-[200px]",
    "2xl:h-[84px] 2xl:w-[216px]",
  );

  const logoImage = logo ? (
    <Image
      alt={logo_alt_text || "Logo"}
      className="block max-w-none size-full object-contain"
      src={logo}
      width={216}
      height={84}
      fetchPriority="high"
      priority
    />
  ) : null;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 bg-white flex items-center justify-between rounded-spacing-10 size-full",
        "sm:px-spacing-36 sm:py-spacing-8",
        "xl:px-spacing-46 xl:py-spacing-6",
        "2xl:px-spacing-56 2xl:py-spacing-8",
      )}
      style={{
        backgroundColor: background_color,
      }}
    >
      <div
        className={cn(
          "flex items-center justify-start min-w-0",
          "sm:gap-spacing-24 ",
          "md:gap-spacing-26 ",
          "lg:gap-spacing-30 ",
          "2xl:gap-spacing-36 ",
        )}
      >
        {logoImage &&
          (logoRedirects ? (
            <Link href="/" className={logoBoxClass}>
              {logoImage}
            </Link>
          ) : (
            <div className={logoBoxClass}>{logoImage}</div>
          ))}

        {promotional_text && (
          <div
            className={cn(
              "relative flex items-center justify-start px-spacing-10 clip-path-arrow overflow-hidden h-[34px] pr-6 text-heading-8-semibold",
              "sm:h-[42px] sm:max-w-[422px] sm:text-body-2-semibold",
              "md:h-[48px] md:max-w-[500px] md:text-body-2-semibold",
              "lg:h-[48px] lg:max-w-[500px] lg:text-heading-7-semibold",
              "xl:h-[50px] xl:max-w-[640px] xl:text-heading-6-semibold",
            )}
            style={{
              backgroundColor: promotional_text_bg_color,
              color: promotional_text_color,
            }}
          >
            <span className="line-clamp-1 w-full type-body-sm">
              {promotional_text}
            </span>
          </div>
        )}
      </div>

      <div
        className={cn(
          "flex items-center gap-spacing-10 text-heading-8-semibold shrink-0",
          "md:text-heading-7-semibold",
          "xl:text-heading-6-semibold",
          "2xl:text-heading-5-semibold",
        )}
      >
        {phone_number && (
          <div className="flex items-center p-1 gap-1.5">
            {phone_icon && (
              <Image
                src={phone_icon || ""}
                alt={phone_icon_alt || "Phone"}
                width={38}
                height={38}
                className={cn(
                  "h-[22px] w-[22px]",
                  "lg:h-[22px] lg:w-[22px]",
                  "xl:h-[34px] xl:w-[34px]",
                )}
              />
            )}
            <a
              href={`tel:+91${phone_number}`}
              className="cursor-pointer type-h6"
              style={{ color: phone_number_text_color }}
            >
              {phone_number}
            </a>
          </div>
        )}

        {button_link && button_name && (
          <Link href={button_link} target="_blank">
            <div
              className={cn(
                "flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold transition-colors hover:opacity-90",
                "sm:h-[40px]",
                "md:h-[40px]",
                "lg:h-[40px]",
                "xl:h-[52px]",
              )}
              style={{
                backgroundColor: button_color,
                color: button_text_color,
              }}
            >
              {button_icon && (
                <Image
                  src={button_icon || ""}
                  alt={button_icon_alt || "WhatsApp"}
                  width={24}
                  height={24}
                  className={cn("xl:size-[34px]")}
                />
              )}
              <span
                className={cn(
                  "sm:text-heading-8-semibold",
                  "md:text-heading-7-semibold",
                  "lg:text-heading-7-semibold",
                  "xl:text-heading-6-semibold",
                  "type-h6",
                )}
              >
                {button_name}
              </span>
            </div>
          </Link>
        )}
      </div>
    </header>
  );
}
